import "ace-builds";
import "ace-builds/webpack-resolver";

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { PageHeader } from 'client/js/util/layout_utils';
import AceEditor from 'react-ace';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { fetchJSON } from 'client/js/util/rest_utils';

// Import ace modes and themes
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/mode-css';
import 'ace-builds/src-noconflict/mode-php';
import 'ace-builds/src-noconflict/mode-ruby';
import 'ace-builds/src-noconflict/mode-text';
import 'ace-builds/src-noconflict/mode-xml';

import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-monokai';
import "ace-builds/src-noconflict/ext-language_tools";

const DEFAULT_BASE_URL = '/usercp/filemanager/api';

const getModeFromExtension = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();
  switch (ext) {
    case 'js':
    case 'jsx':
    case 'ts':
    case 'tsx':
      return 'javascript';
    case 'json':
      return 'json';
    case 'html':
    case 'erb':
      return 'html';
    case 'css':
    case 'scss':
    case 'sass':
      return 'css';
    case 'php':
      return 'php';
    case 'rb':
      return 'ruby';
    default:
      return 'text';
  }
};

const FileEditor = ({ file, onClose, baseApiUrl = DEFAULT_BASE_URL }) => {
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [theme, setTheme] = useState('github');
  const [mode, setMode] = useState(getModeFromExtension(file.name));
  const queryClient = useQueryClient();

  // Track if content has changed
  const hasUnsavedChanges = content !== originalContent;

  // Helper function to encode path for URL
  const encodePath = (path) => {
    return btoa(path).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  };

  // Fetch file content
  const { data, isLoading, error } = useQuery({
    queryKey: ['filemanager', 'content', file.path],
    queryFn: async () => {
      const encodedPath = encodePath(file.path);
      return fetchJSON(`${baseApiUrl}/${encodedPath}`, {
        method: 'GET',
      });
    },
    staleTime: 0, // Always fetch fresh content
  });

  useEffect(() => {
    if (data && data.content !== undefined) {
      setContent(data.content);
      setOriginalContent(data.content);
    }
  }, [data]);

  // Warn before leaving if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const saveFileMutation = useMutation({
    mutationFn: async (newContent) => {
      // We use createItem endpoint (POST to root) with same path to overwrite file content
      // Or check if there is an update endpoint for content.
      // The createItem in `use_filemanager.js` does `fetchJSON(baseApiUrl, { method: 'POST', ... })`
      // The backend `create` action seems to handle overwrite if file exists?
      // Let's check backend implementation. `create_file` raises error if file exists.
      // We probably need to delete and recreate or add an update content endpoint.
      // Wait, the backend `create` method in `LocalFilemanagerBackend` raises "File already exists".
      // But `File.write(new_file, content)` in `create_file` would overwrite if we bypassed the check.
      // The controller `create` action calls `@backend.create_file`.
      //
      // Maybe we should use a PUT request to update content?
      // The `update` action in controller handles 'rename' and 'chmod'.
      // We might need to add a new action type 'update_content' to `update` action or a separate endpoint.
      //
      // Given I cannot easily change backend logic without potentially breaking things or needing review,
      // and I skipped backend zip part, I should check if I can modify backend for this or use existing.
      //
      // Let's look at `app/controllers/usercp/filemanager_controller.rb` again.
      // `create` uses `create_file`.
      // `update` uses `rename` or `chmod`.
      //
      // I should probably add a way to update file content.
      // Since I am the dev, I can add `write_file` or similar to backend and controller.
      //
      // Let's add 'update_content' to `update` action in controller and backend.

      const encodedPath = encodePath(file.path);
      return fetchJSON(`${baseApiUrl}/${encodedPath}`, {
        method: 'PATCH',
        body: JSON.stringify({ action_type: 'update_content', content: newContent }),
      });
    },
    onSuccess: () => {
      setOriginalContent(content); // Update original content after successful save
      queryClient.invalidateQueries({ queryKey: ['filemanager', 'content', file.path] });
      // Also invalidate list if size changed (though less critical)
      const parentPath = file.path.substring(0, file.path.lastIndexOf('/')) || '/';
      queryClient.invalidateQueries({ queryKey: ['filemanager', 'list', parentPath, baseApiUrl] });
      toast.success('Datei erfolgreich gespeichert');
      onClose();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleSave = () => {
    saveFileMutation.mutate(content);
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('Du hast ungespeicherte Änderungen. Möchtest Du wirklich schließen?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  // Available modes for the dropdown
  const availableModes = [
    { value: 'javascript', label: 'JavaScript' },
    { value: 'json', label: 'JSON' },
    { value: 'html', label: 'HTML' },
    { value: 'css', label: 'CSS' },
    { value: 'php', label: 'PHP' },
    { value: 'ruby', label: 'Ruby' },
    { value: 'xml', label: 'XML' },
    { value: 'text', label: 'Plain Text' },
  ];

  const headerButtons = (
    <div className="d-flex align-items-center">
      {/* Theme Toggle */}
      <div className="mr-3">
        <select
          className="form-control form-control-sm"
          value={theme}
          onChange={(e) => setTheme(e.target.value)}
          title="Theme"
        >
          <option value="github">☀️ Light</option>
          <option value="monokai">🌙 Dark</option>
        </select>
      </div>

      {/* Mode Selector */}
      <div className="mr-3">
        <select
          className="form-control form-control-sm"
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          title="Syntax Mode"
        >
          {availableModes.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </div>

      {/* Save Button */}
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={handleSave}
        disabled={saveFileMutation.isLoading || isLoading || !hasUnsavedChanges}
      >
        {saveFileMutation.isLoading ? 'Speichere...' : 'Speichern'}
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <>
        <PageHeader text="Lade Datei..." back_fn={onClose} buttons={headerButtons} />
        <div className="text-center p-5">
          <div className="spinner-wrap"><div className="css-spinloader"></div></div>
          <p className="mt-3">Lade Datei...</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader text="Fehler" back_fn={onClose} buttons={headerButtons} />
        <div className="alert alert-danger m-3">
          <h5>Fehler beim Laden der Datei</h5>
          <p>{error.message}</p>
        </div>
      </>
    );
  }

  return (
    <div
      className="file-editor-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <PageHeader
        text={file.name}
        title={file.name}
        buttons={headerButtons}
        back_fn={onClose}
      />

      <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
        <AceEditor
          mode={mode}
          theme={theme}
          onChange={setContent}
          value={content}
          name="file-editor"
          editorProps={{ $blockScrolling: true }}
          width="100%"
          height="100%"
          fontSize={14}
          showPrintMargin={false}
          showGutter={true}
          highlightActiveLine={true}
          setOptions={{
            enableBasicAutocompletion: true,
            enableLiveAutocompletion: false,
            enableSnippets: false,
            showLineNumbers: true,
            tabSize: 2,
          }}
        />
      </div>
    </div>
  );
};

FileEditor.propTypes = {
  file: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  baseApiUrl: PropTypes.string,
};

export default FileEditor;
